import React from 'react'
import { Button } from '../../components/ui'
import {
  SUPPORT_CENTER_MONTHS,
  SUPPORT_CENTER_WEEKDAYS,
  daysInMonth,
  toggleSortedNumber,
} from '../../components/centralAmparo/scheduleHelpers'
import { buildYearOptions, clampDay, type DateParts } from '../../components/centralAmparo/parametrosHelpers'
import type { SupportCenterHoursByWeekday } from '../../types/supportCenterSpecialties'

type WeekdayHourPickersProps = {
  weekdays: number[]
  hoursByWeekday: SupportCenterHoursByWeekday
  onWeekdays: (next: number[]) => void
  onHoursByWeekday: (next: SupportCenterHoursByWeekday) => void
}

function firstFilledHours(map: SupportCenterHoursByWeekday, excludeDay?: number): number[] {
  for (const d of SUPPORT_CENTER_WEEKDAYS) {
    if (excludeDay != null && d.value === excludeDay) continue
    const hours = map[d.value]
    if (hours?.length) return [...hours]
  }
  return []
}

export const WeekdayHourPickers: React.FC<WeekdayHourPickersProps> = ({
  weekdays,
  hoursByWeekday,
  onWeekdays,
  onHoursByWeekday,
}) => {
  const [activeDay, setActiveDay] = React.useState<number | null>(
    weekdays.length > 0 ? weekdays[0] : null
  )

  React.useEffect(() => {
    if (activeDay != null && weekdays.includes(activeDay)) return
    setActiveDay(weekdays.length > 0 ? weekdays[0] : null)
  }, [weekdays, activeDay])

  const handleWeekdaysChange = (next: number[]) => {
    const prevSet = new Set(weekdays)
    const added = next.filter((d) => !prevSet.has(d))
    const nextMap: SupportCenterHoursByWeekday = { ...hoursByWeekday }

    for (const day of Object.keys(nextMap).map(Number)) {
      if (!next.includes(day)) delete nextMap[day]
    }

    const inherit = firstFilledHours(hoursByWeekday)
    for (const day of added) {
      if (!nextMap[day]?.length) {
        nextMap[day] = inherit.length > 0 ? [...inherit] : []
      }
    }

    onWeekdays(next)
    onHoursByWeekday(nextMap)
    if (added.length > 0) setActiveDay(added[added.length - 1])
    else if (activeDay != null && !next.includes(activeDay)) {
      setActiveDay(next.length > 0 ? next[0] : null)
    }
  }

  const dayHours =
    activeDay != null && weekdays.includes(activeDay)
      ? (hoursByWeekday[activeDay] ?? [])
      : []

  const setDayHours = (hours: number[]) => {
    if (activeDay == null) return
    onHoursByWeekday({ ...hoursByWeekday, [activeDay]: hours })
  }

  const copyToOtherDays = () => {
    if (activeDay == null || dayHours.length === 0) return
    const nextMap: SupportCenterHoursByWeekday = { ...hoursByWeekday }
    for (const day of weekdays) {
      nextMap[day] = [...dayHours]
    }
    onHoursByWeekday(nextMap)
  }

  const activeLabel =
    activeDay != null
      ? SUPPORT_CENTER_WEEKDAYS.find((d) => d.value === activeDay)?.label ?? ''
      : ''

  const formatDayHoursShort = (hours: number[]): string => {
    if (!hours.length) return '—'
    if (hours.length <= 3) {
      return hours.map((h) => `${h.toString().padStart(2, '0')}h`).join(', ')
    }
    const sorted = [...hours].sort((a, b) => a - b)
    return `${sorted[0].toString().padStart(2, '0')}h–${sorted[sorted.length - 1]
      .toString()
      .padStart(2, '0')}h · ${sorted.length}`
  }

  const toggleDay = (day: number) => {
    const next = toggleSortedNumber(weekdays, day)
    handleWeekdaysChange(next)
    if (next.includes(day)) setActiveDay(day)
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/40 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Disponibilidade semanal <span className="text-red-500">*</span>
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Clique no dia para ativar e definir as horas. Cada dia pode ter horário próprio.
          </p>
        </div>
        <div className="flex gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleWeekdaysChange(SUPPORT_CENTER_WEEKDAYS.map((d) => d.value))}
          >
            Todos os dias
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleWeekdaysChange([])}>
            Limpar
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
          {SUPPORT_CENTER_WEEKDAYS.map((d) => {
            const selected = weekdays.includes(d.value)
            const isActive = activeDay === d.value
            const hours = selected ? (hoursByWeekday[d.value] ?? []) : []
            const missingHours = selected && hours.length === 0
            return (
              <button
                key={d.value}
                type="button"
                onClick={() => {
                  if (selected) {
                    setActiveDay(d.value)
                  } else {
                    toggleDay(d.value)
                  }
                }}
                onDoubleClick={() => toggleDay(d.value)}
                title={
                  selected
                    ? 'Clique para editar horas · duplo clique para desmarcar'
                    : 'Clique para marcar este dia'
                }
                className={[
                  'relative flex flex-col items-center justify-center min-h-[4.25rem] rounded-lg border px-1 py-2 transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                  !selected &&
                    'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500',
                  selected &&
                    !isActive &&
                    'border-primary-200 dark:border-primary-800 bg-primary-50/70 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200',
                  selected &&
                    isActive &&
                    'border-primary-500 bg-primary-600 text-white shadow-sm dark:border-primary-400 dark:bg-primary-600',
                  missingHours && !isActive && 'border-amber-300 dark:border-amber-700',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span
                  className={[
                    'text-xs font-semibold tracking-wide uppercase',
                    isActive ? 'text-white' : '',
                  ].join(' ')}
                >
                  {d.label}
                </span>
                <span
                  className={[
                    'mt-1 text-[10px] leading-tight text-center px-0.5',
                    isActive
                      ? 'text-primary-100'
                      : missingHours
                        ? 'text-amber-600 dark:text-amber-400'
                        : selected
                          ? 'text-primary-700/80 dark:text-primary-300/90'
                          : 'text-gray-400 dark:text-gray-500',
                  ].join(' ')}
                >
                  {selected ? formatDayHoursShort(hours) : 'off'}
                </span>
                {selected && (
                  <button
                    type="button"
                    aria-label={`Remover ${d.label}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleDay(d.value)
                    }}
                    className={[
                      'absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full text-[11px] font-bold leading-none',
                      'flex items-center justify-center border shadow-sm',
                      isActive
                        ? 'bg-white text-primary-700 border-white/80'
                        : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-200 dark:border-gray-600 hover:text-red-600',
                    ].join(' ')}
                  >
                    ×
                  </button>
                )}
              </button>
            )
          })}
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 p-3 sm:p-4">
          <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {activeDay == null
                  ? 'Horas do dia'
                  : `Horas — ${activeLabel}`}
                {activeDay != null ? <span className="text-red-500"> *</span> : null}
              </p>
              {activeDay != null && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {dayHours.length === 0
                    ? 'Nenhuma hora selecionada neste dia.'
                    : `${dayHours.length} hora${dayHours.length === 1 ? '' : 's'} selecionada${
                        dayHours.length === 1 ? '' : 's'
                      }.`}
                </p>
              )}
            </div>
            {activeDay != null && (
              <div className="flex gap-1.5 flex-wrap">
                <Button size="sm" variant="ghost" onClick={() => setDayHours([])}>
                  Limpar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={dayHours.length === 0 || weekdays.length < 2}
                  onClick={copyToOtherDays}
                >
                  Copiar p/ outros dias
                </Button>
              </div>
            )}
          </div>

          {activeDay == null ? (
            <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-3 py-8 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Selecione um dia acima para definir o horário.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5">
              {Array.from({ length: 24 }, (_, i) => i).map((h) => {
                const on = dayHours.includes(h)
                return (
                  <button
                    key={h}
                    type="button"
                    onClick={() => setDayHours(toggleSortedNumber(dayHours, h))}
                    className={[
                      'h-9 rounded-md text-xs font-medium tabular-nums transition-colors',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                      on
                        ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700',
                    ].join(' ')}
                  >
                    {h.toString().padStart(2, '0')}h
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type DatePartSelectorsProps = {
  label: string
  parts: DateParts
  onChange: (next: DateParts) => void
  yearOptions?: number[]
}

export const DatePartSelectors: React.FC<DatePartSelectorsProps> = ({
  label,
  parts,
  onChange,
  yearOptions: yearOptionsProp,
}) => {
  const yearOptions = yearOptionsProp ?? buildYearOptions()
  const maxDay = daysInMonth(parts.year, parts.month)
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} <span className="text-red-500">*</span>
      </label>
      <div className="grid grid-cols-3 gap-2">
        <select
          value={parts.day}
          onChange={(e) => onChange(clampDay({ ...parts, day: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {Array.from({ length: maxDay }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
        <select
          value={parts.month}
          onChange={(e) => onChange(clampDay({ ...parts, month: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {SUPPORT_CENTER_MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          value={parts.year}
          onChange={(e) => onChange(clampDay({ ...parts, year: Number(e.target.value) }))}
          className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
